import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { giftCardStore } from '@theme/cart-gift-card';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = await this.#existingDiscounts();
      if (existingDiscounts.some((code) => isSameCode(code, discountCodeValue))) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const data = await this.#updateDiscounts([...existingDiscounts, discountCodeValue], abortController.signal);

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return isSameCode(discount.code, discountCodeValue) && discount.applicable === false;
        })
      ) {
        // Pas un code de réduction applicable : on le retire du panier, puis on le tente comme carte cadeau
        // (l'API panier du thème ne gère pas les cartes cadeaux, voir cart-gift-card.js).
        await this.#updateDiscounts(existingDiscounts, abortController.signal);
        const isGiftCard = await giftCardStore.apply(discountCodeValue);

        discountCode.value = '';
        if (!isGiftCard) this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        // Un code accepté mais sans pastille est un code livraison : il reste sur le panier pour le checkout.
        if (
          !codes.some((code) => typeof code === 'string' && isSameCode(code, discountCodeValue)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return isSameCode(discount.code, discountCodeValue) && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = await this.#existingDiscounts();
      const index = existingDiscounts.findIndex((code) => isSameCode(code, discountCode));
      if (index === -1) return;

      existingDiscounts.splice(index, 1);

      const data = await this.#updateDiscounts(existingDiscounts, abortController.signal);

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Remplace les codes de réduction du panier et renvoie le panier avec le HTML de la section.
   * @param {string[]} codes
   * @param {AbortSignal} signal
   * @returns {Promise<any>}
   */
  async #updateDiscounts(codes, signal) {
    const config = fetchConfig('json', {
      body: JSON.stringify({ discount: codes.join(','), sections: [this.dataset.sectionId] }),
    });

    const response = await fetch(Theme.routes.cart_update_url, { ...config, signal });
    return response.json();
  }

  /**
   * Codes de réduction applicables présents sur le panier. Lus sur le panier et non sur les pastilles :
   * un code livraison (ex. FONCE) n'a pas de pastille et serait sinon retiré à l'ajout d'un autre code.
   * @returns {Promise<string[]>}
   */
  async #existingDiscounts() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();

      return cart.discount_codes
        .filter((/** @type {{ code: string; applicable: boolean; }} */ discount) => discount.applicable)
        .map((/** @type {{ code: string; applicable: boolean; }} */ discount) => discount.code);
    } catch {
      return this.#discountPills();
    }
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #discountPills() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

/**
 * Shopify ne tient pas compte de la casse des codes.
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
function isSameCode(a, b) {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
